import {
  type CustomField,
  type CustomFieldObject,
  type CustomFieldValue,
  getObjectFlags,
} from '@clockwise/clients/custom-fields';
import { DatePicker } from '@clockwise/ui';
import { X } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { useCustomFieldDefinitions } from '@/lib/use-custom-field-definitions';

interface CustomFieldsEditorProps {
  object: CustomFieldObject;
  /** Bag indexed by definition UUID. `unknown` because the host's typed
   *  client lands customFields as `Record<string, unknown>`; the editor
   *  narrows per fieldType internally. */
  values: Record<string, unknown>;
  onChange: (values: Record<string, unknown>) => void;
  /** Server-side field errors keyed by `customFields.<id>`. */
  errors?: Record<string, string>;
  disabled?: boolean;
  /** Optional wrapper className — applied to the outer div when fields exist. */
  className?: string;
  /** Optional test seam. When provided, the editor skips the
   *  `useCustomFieldDefinitions` query and uses these definitions
   *  directly. Lets the component be tested in isolation without
   *  a QueryClient. */
  definitions?: readonly CustomField[];
}

/**
 * Inline editor for the active custom-field definitions of one object.
 * Renders one input per definition (text / number / date / checkbox /
 * select / multi-select). Returns `null` while definitions load if the
 * object has no fields, so it's safe to drop into any form unconditionally.
 *
 * Field-level server errors land in context via the `errors` map — pass
 * the flattened `ValidationError.fields[]` from the failing submit.
 */
export function CustomFieldsEditor({
  object,
  values,
  onChange,
  errors,
  disabled = false,
  className,
  definitions: definitionsProp,
}: CustomFieldsEditorProps): React.ReactElement | null {
  const query = useCustomFieldDefinitions(object);
  const definitions = definitionsProp ?? query.data;
  const isLoading = definitionsProp == null && query.isLoading;

  if (isLoading) {
    return (
      <div className="space-y-3">
        <Skeleton className="h-4 w-24" />
        <Skeleton className="h-10 w-full" />
      </div>
    );
  }

  if (!definitions || definitions.length === 0) return null;

  const setValue = (defId: string, value: CustomFieldValue): void => {
    onChange({ ...values, [defId]: value });
  };

  return (
    <div className={className ?? 'space-y-4'}>
      <h4 className="text-sm font-medium text-muted-foreground">Custom fields</h4>
      {definitions.map((def) => {
        const value = values[def.id];
        const required = getObjectFlags(def, object).required;
        const error = errors?.[`customFields.${def.id}`];

        return (
          <div key={def.id} className="space-y-1">
            <Label htmlFor={`cf-${def.id}`}>
              {def.label}
              {required && <span className="ml-0.5 text-destructive">*</span>}
            </Label>

            {def.fieldType === 'text' && (
              <Input
                id={`cf-${def.id}`}
                value={typeof value === 'string' ? value : ''}
                onChange={(e) => setValue(def.id, e.target.value || null)}
                disabled={disabled}
              />
            )}

            {def.fieldType === 'number' && (
              <Input
                id={`cf-${def.id}`}
                type="number"
                value={typeof value === 'number' ? String(value) : ''}
                onChange={(e) => {
                  const v = e.target.value;
                  setValue(def.id, v === '' ? null : Number(v));
                }}
                disabled={disabled}
              />
            )}

            {def.fieldType === 'date' && (
              <DatePicker
                id={`cf-${def.id}`}
                value={typeof value === 'string' ? value : ''}
                onChange={(v) => setValue(def.id, v || null)}
                clearable={!required}
                disabled={disabled}
                ariaInvalid={error != null}
              />
            )}

            {def.fieldType === 'checkbox' && (
              <div className="flex items-center gap-2 pt-1">
                <input
                  id={`cf-${def.id}`}
                  type="checkbox"
                  className="size-4 rounded border-input"
                  checked={value === true}
                  onChange={(e) => setValue(def.id, e.target.checked)}
                  disabled={disabled}
                />
                <Label htmlFor={`cf-${def.id}`} className="text-sm font-normal">
                  {value === true ? 'Yes' : 'No'}
                </Label>
              </div>
            )}

            {def.fieldType === 'select' && (
              <Select
                value={typeof value === 'string' ? value : ''}
                onValueChange={(v) => setValue(def.id, v || null)}
                disabled={disabled}
              >
                <SelectTrigger id={`cf-${def.id}`}>
                  <SelectValue placeholder="Select…" />
                </SelectTrigger>
                <SelectContent>
                  {(def.options?.choices ?? []).map((choice) => (
                    <SelectItem key={choice} value={choice}>
                      {choice}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}

            {def.fieldType === 'multi_select' && (
              <MultiSelectField
                id={`cf-${def.id}`}
                choices={def.options?.choices ?? []}
                value={Array.isArray(value) ? value : []}
                onChange={(v) => setValue(def.id, v.length > 0 ? v : null)}
                disabled={disabled}
              />
            )}

            {error != null && <p className="text-sm text-destructive">{error}</p>}
          </div>
        );
      })}
    </div>
  );
}

function MultiSelectField({
  id,
  choices,
  value,
  onChange,
  disabled,
}: {
  id: string;
  choices: string[];
  value: string[];
  onChange: (value: string[]) => void;
  disabled: boolean;
}): React.ReactElement {
  const available = choices.filter((c) => !value.includes(c));

  return (
    <div className="space-y-2">
      {value.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {value.map((v) => (
            <span
              key={v}
              className="inline-flex items-center gap-1 rounded bg-muted px-1.5 py-0.5 text-xs"
            >
              {v}
              {!disabled && (
                <button
                  type="button"
                  aria-label={`Remove ${v}`}
                  onClick={() => onChange(value.filter((x) => x !== v))}
                  className="hover:text-destructive"
                >
                  <X className="size-3" />
                </button>
              )}
            </span>
          ))}
        </div>
      )}
      {available.length > 0 && (
        <Select
          value=""
          onValueChange={(v) => {
            if (v) onChange([...value, v]);
          }}
          disabled={disabled}
        >
          <SelectTrigger id={id}>
            <SelectValue placeholder="Add…" />
          </SelectTrigger>
          <SelectContent>
            {available.map((choice) => (
              <SelectItem key={choice} value={choice}>
                {choice}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
    </div>
  );
}
